import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { callPublicApi, callApi } from '../api';
import './QrOkuyucu.css';

// Web Audio API ile sıfır gecikmeli, harici dosyasız sesli geri bildirim
const playSound = (isSuccess) => {
    try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        if (isSuccess) {
            // Hoş iki tonlu başarı bip sesi (A5 -> A6)
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.12);
            gain.gain.setValueAtTime(0.2, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.15);
            if (navigator.vibrate) navigator.vibrate(80);
        } else {
            // Düşük uyarı sesi
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(220, ctx.currentTime);
            gain.gain.setValueAtTime(0.25, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.25);
            if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
        }
    } catch (e) {
        // Ses çalınamazsa sessizce devam et
    }
};

const QrOkuyucu = () => {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState('');
    const [scanStatus, setScanStatus] = useState('idle'); // idle, scanning, processing, success, warning, error
    const [message, setMessage] = useState('');
    const [scannedUser, setScannedUser] = useState(null);
    const [scannedUsersLog, setScannedUsersLog] = useState([]);
    const [manualCode, setManualCode] = useState('');
    const [cameraError, setCameraError] = useState(null);

    const scannerRef = useRef(null);
    const isProcessingRef = useRef(false);

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            const result = await callPublicApi('/etkinlikler');
            if (result && result.items) {
                const validEvents = result.items.filter(e => {
                    const hasTitle = e.baslik && e.baslik.trim().length > 0;
                    const dateVal = e.tarih || e.etkinlikTarihi;
                    const isValidDate = dateVal && !isNaN(new Date(dateVal).getTime());
                    return hasTitle && isValidDate;
                });

                validEvents.sort((a, b) => new Date(b.tarih || b.etkinlikTarihi) - new Date(a.tarih || a.etkinlikTarihi));
                setEvents(validEvents);
            }
        } catch (error) {
            console.error("Etkinlikler yüklenemedi", error);
        }
    };

    const processScan = useCallback(async (code) => {
        const cleanCode = (code || '').trim();
        if (!cleanCode) return;

        if (isProcessingRef.current) return;
        isProcessingRef.current = true;
        setScanStatus('processing');

        try {
            console.log("Yoklama sorgulanıyor:", cleanCode, "Etkinlik ID:", selectedEvent);
            const response = await callApi('/yoklama', 'POST', {
                etkinlikID: selectedEvent || null,
                qrData: cleanCode
            });

            console.log("Yoklama Yanıtı:", response);
            const timestamp = new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

            if (response.success) {
                playSound(true);
                setScanStatus('success');
                setMessage(response.message || 'Giriş Onaylandı');
                setScannedUser(response.user);
                setScannedUsersLog(prev => [
                    { user: response.user, status: 'success', time: timestamp, code: cleanCode },
                    ...prev
                ]);
            } else if (response.type === 'warning') {
                playSound(false);
                setScanStatus('warning');
                setMessage(response.message || 'Mükerrer Giriş!');
                setScannedUser(response.user);
                setScannedUsersLog(prev => [
                    { user: response.user, status: 'warning', time: timestamp, code: cleanCode },
                    ...prev
                ]);
            } else {
                playSound(false);
                setScanStatus('error');
                setMessage(response.message || 'Giriş Başarısız');
                if (response.user) setScannedUser(response.user);
                setScannedUsersLog(prev => [
                    { user: response.user || { ad: 'Bilinmeyen', soyad: '' }, status: 'error', time: timestamp, code: cleanCode },
                    ...prev
                ]);
            }
        } catch (error) {
            console.error("Yoklama Hatası:", error);
            playSound(false);
            setScanStatus('error');
            setMessage(error.message || 'Bağlantı hatası oluştu');
            setScannedUser(null);
        } finally {
            // İşlem bittiğinde kilidi kaldır
            isProcessingRef.current = false;
        }
    }, [selectedEvent]);

    const handleNextScan = () => {
        setScanStatus('scanning');
        setMessage('');
        setScannedUser(null);
        isProcessingRef.current = false;
    };

    // Camera Lifecycle with html5-qrcode
    useEffect(() => {
        let html5QrCode = null;
        let isRunning = false;

        const startCamera = async () => {
            setCameraError(null);
            try {
                // DOM elementinin render edilmesini bekle
                const readerElem = document.getElementById("qr-reader-container");
                if (!readerElem) return;

                html5QrCode = new Html5Qrcode("qr-reader-container");
                scannerRef.current = html5QrCode;

                const config = {
                    fps: 15,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0
                };

                await html5QrCode.start(
                    { facingMode: "environment" },
                    config,
                    (decodedText) => {
                        // Bir kez okunduğunda ve o an işlemde değilse tetikle
                        if (!isProcessingRef.current && scanStatus !== 'processing' && scanStatus !== 'success') {
                            processScan(decodedText);
                        }
                    },
                    () => {
                        // Frame bazlı parse hataları yoksayılır
                    }
                );
                isRunning = true;
                setScanStatus('scanning');
            } catch (err) {
                console.warn("Kamera başlatılamadı:", err);
                let errText = "Kamera başlatılamadı.";
                if (err?.name === "NotAllowedError" || err?.message?.includes("Permission")) {
                    errText = "Kamera izni reddedildi. Lütfen tarayıcı ayarlarından kamera iznini onaylayın.";
                } else if (!window.isSecureContext) {
                    errText = "Mobil tarayıcılar kamerayı sadece HTTPS üzerinden çalıştırır. Lütfen tünel/HTTPS adresini kullanın veya alttan kodu elle girin.";
                }
                setCameraError(errText);
            }
        };

        startCamera();

        return () => {
            if (html5QrCode && isRunning) {
                html5QrCode.stop().then(() => html5QrCode.clear()).catch(e => console.warn("Kamera durdurma uyarısı:", e));
            }
        };
    }, [processScan]);

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualCode.trim()) {
            processScan(manualCode.trim());
            setManualCode('');
        }
    };

    return (
        <div className="scanner-page-wrapper">
            <div className="scanner-wrapper container">

                {/* Sol Kolon: Tarayıcı ve Giriş */}
                <div className="left-col">
                    <div className="scanner-card">
                        <div className="scanner-header">
                            <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#334155' }}>📱 QR Yoklama & Bilet Kontrolü</h3>
                        </div>

                        <div style={{ padding: '15px' }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                                Yoklama Yapılacak Etkinlik:
                            </label>
                            <select
                                className="event-select"
                                value={selectedEvent}
                                onChange={(e) => {
                                    setSelectedEvent(e.target.value);
                                    handleNextScan();
                                }}
                            >
                                <option value="">-- Genel Üye Kartı Doğrulama (Etkinliksiz) --</option>
                                {events.map(e => (
                                    <option key={e.etkinlikID} value={e.etkinlikID}>
                                        {e.baslik}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Tarayıcı Alanı */}
                        <div className="scanner-viewport" style={{ minHeight: '320px', position: 'relative' }}>
                            {/* html5-qrcode video hedefi */}
                            <div id="qr-reader-container" style={{ width: '100%', height: '100%', borderRadius: '16px', overflow: 'hidden' }}></div>

                            {cameraError && (
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    padding: '20px', background: '#1e293b', color: '#f8fafc', textAlign: 'center'
                                }}>
                                    <span style={{ fontSize: '2.5rem', marginBottom: '10px' }}>📷❌</span>
                                    <p style={{ fontSize: '0.9rem', marginBottom: '15px', maxWidth: '300px' }}>{cameraError}</p>
                                    <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Aşağıdaki kutudan üye kodunu elle girebilirsiniz.</p>
                                </div>
                            )}

                            {/* Overlays (Sonuç Ekranları) */}
                            {(scanStatus === 'processing' || scanStatus === 'success' || scanStatus === 'warning' || scanStatus === 'error') && (
                                <div className="qr-overlay">
                                    {scanStatus === 'processing' && (
                                        <div className="status-card">
                                            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                                            <div className="status-title">Sorgulanıyor</div>
                                            <p className="status-message">Bilet ve üyelik doğrulanıyor...</p>
                                        </div>
                                    )}

                                    {scanStatus === 'success' && (
                                        <div className="status-card success-bg">
                                            <div className="status-icon">✅</div>
                                            <div className="status-title success-text">Giriş Onaylandı</div>

                                            <div className="user-info-box">
                                                <p className="user-name">{scannedUser?.ad} {scannedUser?.soyad}</p>
                                                <p className="user-meta"><strong>No:</strong> {scannedUser?.ogrenciNo || '-'}</p>
                                                <p className="user-meta"><strong>Bölüm:</strong> {scannedUser?.bolum || '-'}</p>
                                                <p className="user-meta" style={{ fontSize: '0.75rem', color: '#15803d' }}>
                                                    Kod: {scannedUser?.uyeKodu}
                                                </p>
                                            </div>

                                            <button className="action-btn btn-success" onClick={handleNextScan}>
                                                Sıradaki Kişi 📸
                                            </button>
                                        </div>
                                    )}

                                    {scanStatus === 'warning' && (
                                        <div className="status-card warning-bg">
                                            <div className="status-icon">⚠️</div>
                                            <div className="status-title warning-text">Mükerrer Giriş</div>
                                            <p className="status-message">{message}</p>

                                            {scannedUser && (
                                                <div className="user-info-box">
                                                    <p className="user-name">{scannedUser.ad} {scannedUser.soyad}</p>
                                                    <p className="user-meta">Öğrenci No: {scannedUser.ogrenciNo}</p>
                                                </div>
                                            )}

                                            <button className="action-btn btn-warning" onClick={handleNextScan}>
                                                Devam Et 🔄
                                            </button>
                                        </div>
                                    )}

                                    {scanStatus === 'error' && (
                                        <div className="status-card error-bg">
                                            <div className="status-icon">❌</div>
                                            <div className="status-title error-text">Giriş Başarısız</div>
                                            <p className="status-message">{message}</p>

                                            {scannedUser && (
                                                <div className="user-info-box">
                                                    <p className="user-name">{scannedUser.ad} {scannedUser.soyad}</p>
                                                    <p className="user-meta">{scannedUser.ogrenciNo}</p>
                                                    <p className="user-meta" style={{ color: '#991b1b', fontWeight: 'bold' }}>
                                                        {selectedEvent ? 'Bu Etkinliğe Kayıtlı Değil' : 'Yetkisiz'}
                                                    </p>
                                                </div>
                                            )}

                                            <button className="action-btn btn-danger" onClick={handleNextScan}>
                                                Tekrar Dene 🔄
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Manuel Kod Girişi (Yedek Çözüm) */}
                        <div style={{ padding: '15px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
                            <form onSubmit={handleManualSubmit} style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="text"
                                    placeholder="Veya üye kodunu elle girin (örn: KLU-1004)..."
                                    value={manualCode}
                                    onChange={(e) => setManualCode(e.target.value)}
                                    style={{
                                        flex: 1, padding: '12px 14px', borderRadius: '10px',
                                        border: '1px solid #cbd5e1', fontSize: '0.95rem'
                                    }}
                                />
                                <button
                                    type="submit"
                                    style={{
                                        background: '#ea580c', color: 'white', border: 'none',
                                        borderRadius: '10px', padding: '0 18px', fontWeight: 600,
                                        fontSize: '0.9rem', cursor: 'pointer'
                                    }}
                                >
                                    Kontrol Et
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Sağ Kolon: Canlı Giriş Günlüğü (Log) */}
                <div className="right-col">
                    <div className="scanner-card">
                        <div className="log-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h4 style={{ margin: 0, color: '#475569', fontSize: '1.05rem' }}>📋 Canlı Giriş Günlüğü</h4>
                                <span style={{ fontSize: '0.8rem', background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px', color: '#64748b', fontWeight: 'bold' }}>
                                    Toplam: {scannedUsersLog.length}
                                </span>
                            </div>

                            <div className="log-list" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                                {scannedUsersLog.length === 0 ? (
                                    <p style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0', fontStyle: 'italic' }}>
                                        Henüz okutulan bilet veya üye kartı yok.
                                    </p>
                                ) : (
                                    scannedUsersLog.map((log, index) => (
                                        <div key={index} className={`log-item ${log.status}`}>
                                            <div className="log-user">
                                                <span className="log-name">{log.user?.ad || 'Bilinmeyen'} {log.user?.soyad || ''}</span>
                                                <span className="log-time" style={{ display: 'flex', gap: '8px' }}>
                                                    <span>⏰ {log.time}</span>
                                                    <span>🔑 {log.code}</span>
                                                </span>
                                            </div>
                                            <span className={`log-status`} style={{
                                                color: log.status === 'success' ? '#166534' : (log.status === 'warning' ? '#854d0e' : '#991b1b'),
                                                background: log.status === 'success' ? '#dcfce7' : (log.status === 'warning' ? '#fef9c3' : '#fee2e2'),
                                                padding: '4px 8px', borderRadius: '6px'
                                            }}>
                                                {log.status === 'success' ? 'ONAYLANDI' : (log.status === 'warning' ? 'MÜKERRER' : 'REDDEDİLDİ')}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default QrOkuyucu;
